import { useCallback, useRef, useState } from 'react';

import { DEFAULT_CURRENCY_CODE } from '@/modules/common/constants/currency.constants';

import { MemberStatus, TeamMemberFormData, TeamMemberTab, TeamMemberType } from '../types';
import { InternalRateType } from '../types/team-member';

export interface TeamMemberDraft {
  info?: Partial<TeamMemberFormData>;
  capacity?: Partial<TeamMemberFormData>;
  billing?: Partial<TeamMemberFormData>;
  integrations?: Partial<TeamMemberFormData>;
}

interface DraftOperationParams<T> {
  tab: TeamMemberTab;
  data: Partial<T>;
}

interface UseTeamMemberDraftReturn<T> {
  draft: TeamMemberDraft;
  saveDraftData: (params: DraftOperationParams<T>) => void;
  getDraftData: (tab: TeamMemberTab) => Partial<T> | undefined;
  clearDraft: () => void;
  hasDraftData: boolean;
  getFinalSubmissionData: (defaultCurrency?: string) => TeamMemberFormData;
}

/**
 * Manages team member draft state across multiple modes (Create, Edit, Read) and tabs.
 * Provides persistent form data storage during team member workflow.
 *
 * Features:
 * - Tab-based data persistence for team member info, capacity, billing, and integrations
 * - Automatic state synchronization between React state and refs for immediate access
 * - Draft data validation and cleanup utilities
 * - Final submission data aggregation from all tabs
 * - Works in Create, Edit, and Read modes
 *
 * @template T - The type of data being managed by the hook
 */
export function useTeamMemberDraft<T = TeamMemberFormData>(): UseTeamMemberDraftReturn<T> {
  const [draft, setDraft] = useState<TeamMemberDraft>({});
  const draftRef = useRef<TeamMemberDraft>({});

  draftRef.current = draft;

  const saveDraftData = useCallback(({ tab, data }: DraftOperationParams<T>) => {
    setDraft(prevDraft => ({
      ...prevDraft,
      [tab]: {
        ...prevDraft[tab],
        ...data,
      },
    }));
  }, []);

  const getDraftData = useCallback((tab: TeamMemberTab): Partial<T> | undefined => {
    return draftRef.current[tab] as Partial<T> | undefined;
  }, []);

  const clearDraft = useCallback(() => {
    setDraft({});
  }, []);

  const hasDraftData = Object.keys(draft).some(tab => {
    const tabData = draft[tab as TeamMemberTab];
    return tabData && Object.keys(tabData).length > 0;
  });

  const getFinalSubmissionData = useCallback(
    (defaultCurrency?: string): TeamMemberFormData => {
      const infoData = draft.info || {};
      const capacityData = draft.capacity || {};
      const billingData = draft.billing || {};
      const integrationData = draft.integrations || {};

      return {
        // Info data
        name: infoData.name || '',
        email: infoData.email,
        status: infoData.status || MemberStatus.Active,
        type: infoData.type || TeamMemberType.TeamMember,
        department: infoData.department,
        seniority: infoData.seniority,
        role: infoData.role,
        managerId: infoData.managerId,
        manager: infoData.manager,
        tags: infoData.tags || [],
        avatarLink: infoData.avatarLink,
        country: infoData.country || '',
        city: infoData.city || '',
        phoneNumber: infoData.phoneNumber,
        isManager: infoData.isManager || false,
        about: infoData.about,
        customAttributesValues: infoData.customAttributesValues,

        // Capacity data - default to 8h capacity and 100% billable when capacity tab was never opened
        isOnDemandAvailability: capacityData.isOnDemandAvailability || false,
        capacityPerDay: capacityData.capacityPerDay ?? 8,
        billableCapacity: capacityData.billableCapacity ?? 100,
        corporateFunctionCapacity: capacityData.corporateFunctionCapacity ?? 0,

        // Billing data - use organization default currency as fallback when billing tab was never opened
        internalRateCurrency:
          billingData.internalRateCurrency || defaultCurrency || DEFAULT_CURRENCY_CODE,
        internalRateType: billingData.internalRateType || InternalRateType.MonthlySalary,
        internalRate: billingData.internalRate,
        externalHourlyRate: billingData.externalHourlyRate,

        // Integration data
        integrations: integrationData.integrations || [],
      };
    },
    [draft]
  );

  return {
    draft,
    saveDraftData,
    getDraftData,
    clearDraft,
    hasDraftData,
    getFinalSubmissionData,
  };
}
